import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import moment from 'moment';
import { EntityList, PageHead, Section, DataTable, IdChip, Button } from '../../components/EntityList';
import DetailPageLoader from '../../components/DetailPageLoader/DetailPageLoader';
import generatedDebtService from '../../services/generatedDebts.service';
import { getError } from '../../utils';
import { useLabels } from '../../hooks/useLabels';
import ConfirmCountModal from './ConfirmCountModal';

/**
 * Dues of per-user debts awaiting their monthly user-count confirmation.
 */
export default function PendingCountDues() {
	const { t } = useLabels();
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	const [dues, setDues] = useState([]);
	const [selectedDue, setSelectedDue] = useState(null);
	const [showConfirm, setShowConfirm] = useState(false);

	async function fetchData() {
		try {
			setLoading(true);
			const { data } = await generatedDebtService.getPendingCount();
			setDues(data);
			setError('');
		} catch (err) {
			setError(getError(err));
		}
		setLoading(false);
	}

	useEffect(() => {
		fetchData();
	}, []);

	const columns = [
		{ key: 'customId', header: t('entity.due'), render: (due) => <IdChip>{due.customId}</IdChip> },
		{ key: 'debt', header: t('field.accountConcept'), render: (due) => due.debt?.customId || due.debt?.owner?.name || '—' },
		{ key: 'date', header: t('field.date'), render: (due) => moment.utc(due.date).format('DD/MM/YYYY') },
		{
			key: 'pricePerUser',
			header: t('form.pricePerUser'),
			className: 'tw:text-right',
			render: (due) => <span className='tw:font-mono tw:tabular-nums'>{due.pricePerUserSnapshot ?? '—'}</span>,
		},
		{
			key: 'userCount',
			header: t('field.lastCount'),
			className: 'tw:text-right',
			render: (due) => <span className='tw:font-mono tw:tabular-nums'>{due.userCount ?? '—'}</span>,
		},
		{
			key: 'amount',
			header: t('field.provisionalAmount'),
			className: 'tw:text-right',
			render: (due) => <span className='tw:font-mono tw:tabular-nums'>{due.amount}</span>,
		},
	];

	const renderRowActions = (due) => (
		<Button
			size='sm'
			onClick={() => {
				setSelectedDue(due);
				setShowConfirm(true);
			}}
		>
			{t('action.confirmUsers')}
		</Button>
	);

	return (
		<EntityList>
			<Helmet>
				<title>{t('title.pendingCountDues')}</title>
			</Helmet>
			<PageHead
				breadcrumb={[
					{ label: 'Inicio', to: '/AdminScreen' },
					{ label: t('entity.debt.plural'), to: '/AdminScreen/debts' },
					{ label: t('title.pendingCountDues') },
				]}
				title={t('title.pendingCountDues')}
				subtitle={`${t('entity.due.plural')} de facturación por usuario que esperan la cantidad de usuarios del mes.`}
			/>

			{loading ? (
				<DetailPageLoader entityLabel={t('entity.due.plural')} />
			) : error ? (
				<div className='tw:rounded-[7px] tw:border tw:border-danger tw:bg-danger-soft tw:px-4 tw:py-3 tw:text-[13px] tw:text-danger'>
					{error}
				</div>
			) : (
				<Section title={t('title.pendingCountDues')}>
					<DataTable
						columns={columns}
						rows={dues}
						loading={false}
						error=''
						rowActions={renderRowActions}
						emptyMessage={t('msg.noPendingCountDues')}
					/>
				</Section>
			)}

			<ConfirmCountModal
				show={showConfirm}
				onHide={() => {
					setShowConfirm(false);
					setSelectedDue(null);
				}}
				due={selectedDue}
				onSaved={fetchData}
			/>
		</EntityList>
	);
}
